import React, { useEffect, useState } from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useInvoice } from '../contexts/InvoiceContext';
import { Invoice, INVOICE_STATUS_CONFIG } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';
import InvoiceViewModal from './InvoiceViewModal';
import { formatDateAU, isDateOverdue, daysOverdueCount } from '../../../utils/dateUtils';

interface InvoiceListProps {
  onSelectContact?: (contactId: string, contactName: string) => void;
  onEditInvoice?: (invoice: Invoice) => void;
  onCopyInvoice?: (invoice: Invoice) => void;
  onNewInvoice?: () => void;
  filterContent?: React.ReactNode;
}

const formatDateStr = formatDateAU;

export function InvoiceList({ onSelectContact, onEditInvoice, onCopyInvoice, onNewInvoice, filterContent }: InvoiceListProps) {
  const {
    invoices,
    invoicesLoading,
    invoicesError,
    fetchInvoices,
    invoiceFilters,
    toggleInvoiceSent,
    toggleSentLoading,
    voidInvoice,
    deleteDraftInvoice,
    approveInvoice,
    emailInvoice,
    getOnlineInvoiceUrl,
    invoiceActionLoading,
  } = useInvoice();

  const [sortField, setSortField] = useState<keyof Invoice | 'contactName'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'void' | 'delete'; invoice: Invoice } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const isMobile = useIsMobile(768);

  const handleToggleSent = async (invoice: Invoice) => {
    setToggleError(null);
    try {
      await toggleInvoiceSent(invoice.id, !invoice.sentToContact);
    } catch (err: any) {
      setToggleError(err?.message || 'Failed to update sent status');
      setTimeout(() => setToggleError(null), 3000);
    }
  };

  const handleVoid = async (invoice: Invoice) => {
    setConfirmAction(null);
    setActionError(null);
    try {
      await voidInvoice(invoice.id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to void invoice');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleDeleteDraft = async (invoice: Invoice) => {
    setConfirmAction(null);
    setActionError(null);
    try {
      await deleteDraftInvoice(invoice.id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to delete invoice');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleApprove = async (invoice: Invoice) => {
    setActionError(null);
    try {
      await approveInvoice(invoice.id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to approve invoice');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleEmail = async (invoice: Invoice) => {
    setActionError(null);
    try {
      const { to } = await emailInvoice(invoice.id);
      toast.success(`Drafted an email${to ? ` to ${to}` : ''} with the invoice attached — review & send it from your Drafts.`);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to draft the invoice email');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleCopyLink = async (invoice: Invoice) => {
    setActionError(null);
    setCopySuccess(null);
    try {
      const url = await getOnlineInvoiceUrl(invoice.id);
      if (url) {
        await navigator.clipboard.writeText(url);
        setCopySuccess(invoice.id);
        setTimeout(() => setCopySuccess(null), 2000);
      } else {
        setActionError('Online invoice URL not available');
        setTimeout(() => setActionError(null), 3000);
      }
    } catch (err: any) {
      setActionError(err?.message || 'Failed to get link');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const isOverdue = (invoice: Invoice) => {
    if ((invoice.status !== 'APPROVED' && invoice.status !== 'SENT') || !invoice.amountDue) return false;
    return isDateOverdue(invoice.dueDate);
  };

  const daysOverdue = (invoice: Invoice): number => daysOverdueCount(invoice.dueDate);

  const handleSort = (field: keyof Invoice | 'contactName') => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDir(field === 'dueDate' || field === 'date' ? 'desc' : 'asc');
    }
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    // Unsent invoices first (only among awaiting-payment invoices)
    const aAwait = a.status === 'APPROVED' || a.status === 'SENT';
    const bAwait = b.status === 'APPROVED' || b.status === 'SENT';
    if (aAwait && bAwait) {
      const aSent = a.sentToContact ? 1 : 0;
      const bSent = b.sentToContact ? 1 : 0;
      if (aSent !== bSent) return aSent - bSent;
    }

    let aVal: any = sortField === 'contactName' ? a.contactName : (a as any)[sortField];
    let bVal: any = sortField === 'contactName' ? b.contactName : (b as any)[sortField];
    if (sortField === 'dueDate' || sortField === 'date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const strA = String(aVal || '').toLowerCase();
    const strB = String(bVal || '').toLowerCase();
    return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
  });

  const totalCount = sortedInvoices.length;
  // Total still owing across the currently-filtered invoices. amountDue is 0 for
  // paid invoices, so this reads correctly under any filter (Awaiting Payment,
  // all, etc.) as the outstanding receivable.
  const totalOwing = sortedInvoices.reduce((sum, inv) => sum + (Number(inv.amountDue) || 0), 0);
  const owingCurrency = sortedInvoices[0]?.currencyCode || 'AUD';
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedInvoices = sortedInvoices.slice(startIdx, startIdx + pageSize);

  const statusesFilter = Array.isArray(invoiceFilters.status) ? invoiceFilters.status : invoiceFilters.status ? [invoiceFilters.status] : [];

  const SortHeader = ({ field, label }: { field: keyof Invoice | 'contactName'; label: string }) => (
    <th
      className="min-w-100px cursor-pointer"
      onClick={() => handleSort(field)}
    >
      <div className="d-flex align-items-center">
        {label}
        {sortField === field && (
          <i className={`bi bi-chevron-${sortDir === 'asc' ? 'up' : 'down'} ms-1`}></i>
        )}
      </div>
    </th>
  );

  if (invoicesLoading) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Invoices</span>
            <span className="text-muted mt-1 fw-semibold fs-7">Loading…</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-3">
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Amount Due</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th className="text-center">Sent</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <tr key={i}>
                    <td colSpan={8}>
                      <span className="placeholder col-12" style={{ height: 20 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="placeholder-glow mt-2">
            <span className="placeholder col-3" style={{ height: 14 }} />
          </div>
        </div>
      </div>
    );
  }

  if (invoicesError) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Invoices</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-10 text-center">
          <i className="bi bi-exclamation-triangle text-danger fs-1"></i>
          <p className="text-danger mt-3">{invoicesError}</p>
          <button className="btn btn-primary btn-sm" onClick={() => fetchInvoices()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Invoices</span>
            <span className="text-muted mt-1 fw-semibold fs-7">0 invoices</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-10 text-center">
          <i className="bi bi-file-earmark-check text-muted fs-1"></i>
          <p className="text-muted mt-3">
            {statusesFilter.length > 0
              ? 'No invoices match the selected filters.'
              : 'No invoices found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header border-0 pt-4 pt-md-5 px-4 px-md-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">Invoices</span>
          <span className="text-muted mt-1 fw-semibold fs-7">
            {totalCount} invoice{totalCount !== 1 ? 's' : ''}
          </span>
        </h3>
        {totalOwing > 0 && (
          <div className="d-flex flex-column justify-content-center align-items-start align-items-md-center flex-grow-1 px-md-4 my-2 my-md-0">
            <span className="text-muted fw-semibold fs-8 text-uppercase" style={{ letterSpacing: '0.05em' }}>
              Total owing
            </span>
            <span className="fw-bold fs-3 text-warning">
              {formatCurrency(totalOwing, owingCurrency)}
            </span>
          </div>
        )}
        {onNewInvoice && (
          <div className="card-toolbar accounting-card-toolbar">
            <button className="btn btn-sm btn-success" onClick={onNewInvoice}>
              <i className="bi bi-plus-lg me-2"></i>
              New Invoice
            </button>
          </div>
        )}
      </div>
      {filterContent}
      <div className="card-body py-3">
        {(toggleError || actionError) && (
          <div className="alert alert-danger mb-3 py-2">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {toggleError || actionError}
          </div>
        )}
        {copySuccess && (
          <div className="alert alert-success mb-3 py-2">
            <i className="bi bi-check-circle me-2"></i>
            Link copied to clipboard
          </div>
        )}

        {/* Mobile: card list */}
        {isMobile && (
          <div className="d-md-none d-flex flex-column gap-2">
            {paginatedInvoices.map((invoice) => {
              const config = INVOICE_STATUS_CONFIG[invoice.status];
              const overdue = isOverdue(invoice);
              return (
                <div
                  key={invoice.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedInvoice(invoice)}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedInvoice(invoice)}
                  className="border rounded-3 p-3 bg-body cursor-pointer invoice-mobile-card"
                  style={{ transition: 'background-color 0.15s' }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="fw-bold fs-6 text-gray-800">{invoice.invoiceNumber}</span>
                    <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                  </div>
                  <div className="text-muted fs-8 mb-1">{invoice.contactName || '—'}</div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`fw-bold ${overdue ? 'text-danger' : 'text-gray-800'}`}>
                      {formatCurrency(invoice.amountDue, invoice.currencyCode)}
                    </span>
                    <span className={`fs-8 ${overdue ? 'text-danger' : 'text-muted'}`}>
                      Due {formatDateStr(invoice.dueDate)}
                      {overdue && (
                        <span className="ms-1 fw-bold">({daysOverdue(invoice)}d)</span>
                      )}
                    </span>
                  </div>
                  <div className="mt-2 pt-2 border-top d-flex align-items-center justify-content-between">
                    <span className="fs-8 text-muted">
                      {invoice.sentToContact ? (
                        <i className="bi bi-check-circle-fill text-success me-1" />
                      ) : (
                        <i className="bi bi-circle text-muted me-1" />
                      )}
                      {invoice.sentToContact ? 'Sent' : 'Not sent'}
                    </span>
                    <i className="bi bi-chevron-right text-muted" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`table-responsive ${isMobile ? 'd-none' : ''}`}>
          <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
              <tr className="fw-bold text-muted">
                <SortHeader field="invoiceNumber" label="Invoice #" />
                <th className="min-w-150px cursor-pointer" onClick={() => handleSort('contactName')}>
                  <div className="d-flex align-items-center">
                    Customer
                    {sortField === 'contactName' && (
                      <i className={`bi bi-chevron-${sortDir === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </div>
                </th>
                <SortHeader field="total" label="Total" />
                <SortHeader field="amountDue" label="Amount Due" />
                <SortHeader field="dueDate" label="Due Date" />
                <th className="min-w-100px">Status</th>
                <th className="min-w-80px text-center">Sent</th>
                <th className="min-w-80px text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice) => {
                const config = INVOICE_STATUS_CONFIG[invoice.status];
                const overdue = isOverdue(invoice);
                return (
                  <tr key={invoice.id}>
                    <td>
                      <button
                        className="btn btn-link p-0 text-start text-hover-primary fw-bold fs-6"
                        onClick={() => setSelectedInvoice(invoice)}
                        title="View / Download"
                      >
                        {invoice.invoiceNumber}
                      </button>
                      {invoice.reference && (
                        <span className="text-muted d-block fs-7">{invoice.reference}</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-link p-0 text-start text-hover-primary"
                        onClick={() => onSelectContact?.(invoice.contactId || '', invoice.contactName || '')}
                      >
                        {invoice.contactName || '—'}
                      </button>
                    </td>
                    <td>
                      <span className="text-dark fw-semibold">
                        {formatCurrency(invoice.total, invoice.currencyCode)}
                      </span>
                    </td>
                    <td>
                      <span className={`text-dark fw-bold ${overdue ? 'text-danger' : ''}`}>
                        {formatCurrency(invoice.amountDue, invoice.currencyCode)}
                      </span>
                    </td>
                    <td>
                      <span className={overdue ? 'text-danger fw-semibold' : ''}>
                        {formatDateStr(invoice.dueDate)}
                        {overdue && (
                          <span className="badge badge-light-danger ms-2" title={`${daysOverdue(invoice)} days overdue`}>
                            {daysOverdue(invoice)}d overdue
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                    </td>
                    <td className="text-center">
                      <button
                        className={`btn btn-sm btn-icon ${invoice.sentToContact ? 'btn-light-success' : 'btn-light-secondary'}`}
                        onClick={() => handleToggleSent(invoice)}
                        disabled={toggleSentLoading === invoice.id}
                        title={invoice.sentToContact ? 'Marked as sent' : 'Not sent - click to mark as sent'}
                      >
                        {toggleSentLoading === invoice.id ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : invoice.sentToContact ? (
                          <i className="bi bi-check-circle-fill text-success"></i>
                        ) : (
                          <i className="bi bi-circle text-muted"></i>
                        )}
                      </button>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <button
                          className="btn btn-sm btn-icon btn-light-primary"
                          onClick={() => onEditInvoice?.(invoice)}
                          title="Edit invoice"
                          disabled={invoice.status === 'PAID' || invoice.status === 'VOIDED'}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="light"
                            size="sm"
                            className="btn-icon"
                            id={`invoice-actions-${invoice.id}`}
                            disabled={!!invoiceActionLoading}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {onCopyInvoice && (
                              <Dropdown.Item
                                onClick={() => onCopyInvoice(invoice)}
                              >
                                <i className="bi bi-copy me-2 text-info"></i>
                                Copy to new invoice
                              </Dropdown.Item>
                            )}
                            {onCopyInvoice && <Dropdown.Divider />}
                            {invoice.status === 'DRAFT' && (
                              <Dropdown.Item
                                onClick={() => handleApprove(invoice)}
                                disabled={invoiceActionLoading === invoice.id}
                              >
                                <i className="bi bi-check-circle me-2 text-success"></i>
                                Approve (Awaiting Payment)
                              </Dropdown.Item>
                            )}
                            {(invoice.status === 'APPROVED' || invoice.status === 'SENT') && (
                              <Dropdown.Item
                                onClick={() => setConfirmAction({ type: 'void', invoice })}
                                disabled={invoiceActionLoading === invoice.id}
                              >
                                <i className="bi bi-x-circle me-2"></i>
                                Void invoice
                              </Dropdown.Item>
                            )}
                            {invoice.status === 'DRAFT' && (
                              <Dropdown.Item
                                onClick={() => setConfirmAction({ type: 'delete', invoice })}
                                disabled={invoiceActionLoading === invoice.id}
                                className="text-danger"
                              >
                                <i className="bi bi-trash me-2"></i>
                                Delete draft
                              </Dropdown.Item>
                            )}
                            {invoice.status !== 'PAID' && invoice.status !== 'VOIDED' && (
                              <Dropdown.Item
                                onClick={() => handleEmail(invoice)}
                                disabled={invoiceActionLoading === invoice.id}
                              >
                                <i className="bi bi-envelope me-2"></i>
                                Email invoice
                              </Dropdown.Item>
                            )}
                            {(invoice.status === 'APPROVED' || invoice.status === 'PAID') && (
                              <Dropdown.Item
                                onClick={() => handleCopyLink(invoice)}
                                disabled={invoiceActionLoading === invoice.id}
                              >
                                <i className="bi bi-link-45deg me-2"></i>
                                Copy online link
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-3 border-top">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fs-8">
                Showing {startIdx + 1}–{Math.min(startIdx + pageSize, totalCount)} of {totalCount}
              </span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted fs-8">per page</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <span className="px-2 fs-8 text-muted">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>

      <InvoiceViewModal
        show={!!selectedInvoice}
        invoice={selectedInvoice}
        onHide={() => setSelectedInvoice(null)}
      />

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction?.type === 'void' ? 'Void invoice' : 'Delete draft'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction && (
            <p className="mb-0">
                {confirmAction.type === 'void'
                ? `Are you sure you want to void invoice ${confirmAction.invoice.invoiceNumber}? This cannot be undone.`
                : `Are you sure you want to delete draft invoice ${confirmAction.invoice.invoiceNumber}? This cannot be undone.`}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" onClick={() => setConfirmAction(null)}>
            Cancel
          </button>
          <button
            className={confirmAction?.type === 'delete' ? 'btn btn-danger' : 'btn btn-warning'}
            onClick={() => confirmAction && (confirmAction.type === 'void' ? handleVoid(confirmAction.invoice) : handleDeleteDraft(confirmAction.invoice))}
          >
            {confirmAction?.type === 'void' ? 'Void' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default InvoiceList;
